import React, { useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Check, Globe, Shield, ArrowRight, Star, HardDrive, Zap } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

export const HostingPage = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash !== '#pricing') return undefined;

    const frame = window.requestAnimationFrame(() => {
      document.getElementById('pricing')?.scrollIntoView({ block: 'start' });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [location.hash]);

  const plans = [
    {
      name: 'Business',
      description: 'Perfect for personal websites and blogs',
      monthlyPrice: 5.00,
      yearlyPrice: 60.00,
      popular: false,
      orderUrl: 'https://961host.net/order/cart.php?a=add&pid=171',
      features: [
        '2 GB SSD Storage',
        'Unlimited Bandwidth',
        '3 Email Accounts',
        '1 SQL Database',
        'Unlimited FTP Accounts',
        'Unlimited Subdomains',
        'Unlimited Parked Domains',
        'Unlimited Add-on Domains',
        "Let's Encrypt SSL (Free)",
      ],
    },
    {
      name: 'Corporate',
      description: 'Ideal for growing businesses',
      monthlyPrice: 8.00,
      yearlyPrice: 96.00,
      popular: true,
      orderUrl: 'https://961host.net/order/cart.php?a=add&pid=297',
      features: [
        '5 GB SSD Storage',
        'Unlimited Bandwidth',
        '10 Email Accounts',
        '3 SQL Databases',
        'Unlimited FTP Accounts',
        'Unlimited Subdomains',
        'Unlimited Parked Domains',
        'Unlimited Add-on Domains',
        "Let's Encrypt SSL (Free)",
      ],
    },
    {
      name: 'Professional',
      description: 'For power users and agencies',
      monthlyPrice: 12.00,
      yearlyPrice: 140.00,
      popular: false,
      orderUrl: 'https://961host.net/order/cart.php?a=add&pid=172',
      features: [
        '10 GB SSD Storage',
        'Unlimited Bandwidth',
        'Unlimited Email Accounts',
        'Unlimited SQL Databases',
        'Unlimited FTP Accounts',
        'Unlimited Subdomains',
        'Unlimited Parked Domains',
        'Unlimited Add-on Domains',
        "Let's Encrypt SSL (Free)",
      ],
    },
  ];

  const annualFeatures = ['Free Domain (Annual)*', 'Free Privacy Protection*'];

  const renderPlans = (billingPeriod) => {
    const isYearly = billingPeriod === 'yearly';
    const billingCycle = isYearly ? 'annually' : 'monthly';

    return (
      <>
        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {plans.map((plan) => {
            const price = isYearly ? plan.yearlyPrice : plan.monthlyPrice;
            const features = isYearly ? [...plan.features, ...annualFeatures] : plan.features;

            return (
              <Card
                key={plan.name}
                className={`relative flex flex-col transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${
                  plan.popular
                    ? 'border-2 border-primary shadow-lg scale-105'
                    : 'border border-border'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-primary text-white px-4 py-1 shadow-md">
                      <Star className="w-3 h-3 mr-1 inline fill-current" />
                      Best Value
                    </Badge>
                  </div>
                )}

                <CardHeader className="text-center pb-6 pt-8">
                  <h3 className="text-xl font-semibold text-foreground mb-2">{plan.name}</h3>
                  <p className="text-sm text-muted-foreground mb-4">{plan.description}</p>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-5xl font-semibold text-foreground">${price.toFixed(2)}</span>
                    <span className="text-muted-foreground">/{isYearly ? 'yr' : 'mo'}</span>
                  </div>
                </CardHeader>

                <CardContent className="flex-1 flex flex-col space-y-6 px-6 pb-8">
                  <ul className="space-y-3 flex-1">
                    {features.map((feature) => (
                      <li key={feature} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-primary shrink-0 mt-0.5" strokeWidth={2.5} />
                        <span className="text-sm text-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    size="lg"
                    className="w-full mt-auto"
                    variant={plan.popular ? 'default' : 'outline'}
                    data-testid={`hosting-${plan.name.toLowerCase()}-btn`}
                    asChild
                  >
                    <a
                      href={`${plan.orderUrl}&billingcycle=${billingCycle}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Order Now
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </a>
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {isYearly && (
          <div className="mt-12 text-center">
            <p className="text-muted-foreground text-sm max-w-2xl mx-auto">
              * Free domain name and privacy protection available with annual hosting plans.{' '}
              Register your very own domain name when you sign up!
            </p>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="min-h-screen pt-16">
      {/* Hero Section */}
      <section className="py-24 bg-foreground relative overflow-hidden">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-white/5 rounded-full blur-3xl" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="bg-white/10 backdrop-blur-sm border-white/20 text-white hover:bg-white/20 mb-6">
            <Zap className="w-4 h-4 mr-2" />
            All Plans Include Free SSL & cPanel
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-white mb-6">
            Shared Hosting Plans
          </h1>
          <p className="text-xl text-white/70 max-w-3xl mx-auto">
            Fast, reliable, and affordable web hosting powered by LiteSpeed servers
          </p>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="scroll-mt-16 py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs defaultValue="monthly">
            <div className="flex justify-center">
              <TabsList aria-label="Billing period" className="h-11">
                <TabsTrigger value="monthly" className="px-6 py-2" data-testid="hosting-monthly-tab">
                  Monthly
                </TabsTrigger>
                <TabsTrigger value="yearly" className="px-6 py-2" data-testid="hosting-yearly-tab">
                  Yearly
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="monthly" className="mt-10">
              {renderPlans('monthly')}
            </TabsContent>
            <TabsContent value="yearly" className="mt-10">
              {renderPlans('yearly')}
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-secondary/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-semibold text-foreground mb-4">
              All Plans Include
            </h2>
            <p className="text-muted-foreground">Premium features with every hosting plan</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Zap, title: 'LiteSpeed Servers', desc: 'Up to 40x faster performance' },
              { icon: Shield, title: 'Free SSL Certificate', desc: "Let's Encrypt included" },
              { icon: Globe, title: 'cPanel Control', desc: 'Easy website management' },
              { icon: HardDrive, title: 'SSD Storage', desc: 'Fast and reliable' },
            ].map((feature, idx) => {
              const Icon = feature.icon;
              return (
                <Card key={idx} className="card-premium text-center">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                      <Icon className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="font-semibold text-foreground mb-2">{feature.title}</h3>
                    <p className="text-sm text-muted-foreground">{feature.desc}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* WordPress CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-semibold text-foreground mb-4">
            Looking for WordPress Hosting?
          </h2>
          <p className="text-lg text-muted-foreground mb-8">
            All our plans support one-click WordPress installation with Softaculous
          </p>
          <Button size="lg" asChild>
            <Link to="/wordpress">
              Learn More About WordPress
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </Button>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-20 bg-foreground text-center">
        <div className="max-w-4xl mx-auto px-4">
          <Shield className="w-16 h-16 text-white/80 mx-auto mb-6" />
          <h2 className="text-3xl sm:text-4xl font-semibold text-white mb-4">
            Need Help Choosing?
          </h2>
          <p className="text-xl text-white/70 mb-8">
            Our team is here to help you find the perfect hosting solution
          </p>
          <Button size="lg" variant="primary-light" asChild data-testid="hosting-contact-btn">
            <Link to="/contact">Contact Us</Link>
          </Button>
        </div>
      </section>
    </div>
  );
};
